import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, query, where, orderBy, getDocs, doc, deleteDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import styled from 'styled-components';
import { 
  FaPlus, 
  FaVideo, 
  FaTrash, 
  FaEdit, 
  FaCheck, 
  FaTimes, 
  FaSave,
  FaUpload,
  FaSpinner,
  FaYoutube,
  FaLink,
  FaEye,
  FaBell
} from 'react-icons/fa';

const Container = styled.div`
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  background: #f8f9fa;
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  color: #292929;
  margin: 0;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #6c63ff;
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #291759, #6c63ff);
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  box-shadow: 0 4px 10px rgba(108, 99, 255, 0.3);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(108, 99, 255, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  padding: 1.75rem;
  margin-bottom: 2rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(0,0,0,0.03);
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  }
`;

const VideoThumbnail = styled.div`
  height: 180px;
  background: #eee;
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.2);
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255,255,255,0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  svg {
    color: #f00;
    font-size: 24px;
  }
`;

const VideoInfo = styled.div`
  padding: 1.25rem;
`;

const VideoTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.1rem;
`;

const VideoDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const VideoActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #eee;
  background: #f9fafb;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.color || '#6c63ff'};
  color: white;
  border: none;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 700px;
  width: 95%;
  max-height: 85vh;
  overflow: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f1f3f4;
  padding-bottom: 1rem;
`;

const ModalTitle = styled.h2`
  color: #6c63ff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #6c63ff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #6c63ff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #6c63ff;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  
  &:hover {
    background: #218838;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  
  &:hover {
    background: #545b62;
  }
`;

const LoadingDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #666;
`;

const ErrorDiv = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const SuccessDiv = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background-color: #e9ecef;
  border-radius: 5px;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #6c63ff;
  width: ${props => `${props.progress}%`};
  transition: width 0.2s ease-in-out;
`;

const FileDrop = styled.div`
  border: 2px dashed ${props => props.isDragging ? '#6c63ff' : '#e1e5e9'};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  background: ${props => props.isDragging ? 'rgba(108, 99, 255, 0.05)' : '#f8f9fa'};
  transition: all 0.3s;
  
  &:hover {
    border-color: #6c63ff;
    background: rgba(108, 99, 255, 0.05);
  }
`;

const VideoTypeTab = styled.div`
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
`;

const TypeTabButton = styled.button`
  flex: 1;
  border: none;
  padding: 0.75rem;
  background: ${props => props.active ? '#6c63ff' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  
  &:hover {
    background: ${props => props.active ? '#6c63ff' : '#f1f3f4'};
  }
`;

const VideosManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Video upload states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('file'); // 'file' or 'link'
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [videoCategory, setVideoCategory] = useState('workout');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [videoPreference, setVideoPreference] = useState('1');

  // Upload progress
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Drag and drop
  const [isDraggingVideo, setIsDraggingVideo] = useState(false);
  const [isDraggingThumbnail, setIsDraggingThumbnail] = useState(false);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  
  // Add notification state
  const [notifications, setNotifications] = useState(0);
  
  useEffect(() => {
    if (currentUser) {
      loadVideos();
    } else {
      navigate('/login');
    }
  }, [currentUser]);
  
  useEffect(() => {
    if (!currentUser) return;
    
    // Set up listener for new chat messages - Fix the syntax to use query
    const chatQuery = query(
      collection(db, 'chats'),
      where('partnerId', '==', currentUser.uid),
      where('read', '==', false)
    );
    
    const unsubscribe = onSnapshot(
      chatQuery,
      (snapshot) => {
        setNotifications(snapshot.docs.length);
      },
      (error) => {
        console.error("Chat notification error:", error);
      }
    );
    
    return () => unsubscribe();
  }, [currentUser]);
  
  const loadVideos = async () => {
    try {
      setLoading(true);
      const videosRef = collection(db, 'videos');
      const q = query(
        videosRef,
        where('partnerId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const videosList = [];
      
      querySnapshot.forEach((doc) => {
        videosList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setVideos(videosList);
    } catch (err) {
      console.error('Error loading videos:', err);
      setError('Failed to load videos. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
const handleFileUpload = async (e) => {
  e.preventDefault();

  if (uploadType === 'link' && !videoLink) {
    setError('Please enter a valid video URL');
    return;
  }

  // ✅ Only force videoFile if it's not edit mode
  if (uploadType === 'file' && !videoFile && !editMode) {
    setError('Please select a video file to upload');
    return;
  }

  if (!videoTitle) {
    setError('Please enter a video title');
    return;
  }

  try {
    setUploading(true);
    setError('');

    let videoUrl = '';
    let thumbnailUrl = '';

    if (uploadType === 'file') {
      if (videoFile) {
        try {
          console.log('Starting video upload process...');
          const formData = new FormData();
          formData.append('file', videoFile, videoFile.name);
          formData.append('userId', currentUser.uid);
          formData.append('folder', 'videos');

          const xhr = new XMLHttpRequest();

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = (event.loaded / event.total) * 100;
              setUploadProgress(progress);
              console.log(`Upload progress: ${Math.round(progress)}%`);
            }
          };

          const uploadPromise = new Promise((resolve, reject) => {
            xhr.onload = () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const response = JSON.parse(xhr.responseText);
                  resolve(response);
                } catch (e) {
                  reject(new Error('Invalid response format'));
                }
              } else {
                try {
                  const errorResponse = JSON.parse(xhr.responseText);
                  reject(new Error(errorResponse.error || `Upload failed with status ${xhr.status}`));
                } catch (e) {
                  reject(new Error(`Upload failed with status ${xhr.status}`));
                }
              }
            };

            xhr.onerror = () => reject(new Error('Network error during upload'));
            xhr.ontimeout = () => reject(new Error('Upload timed out'));
          });

          xhr.open('POST', 'https://uploadvideo-jzyeayelrq-uc.a.run.app');
          xhr.send(formData);

          const uploadResult = await uploadPromise;
          videoUrl = uploadResult.url;
        } catch (uploadError) {
          setError(`Failed to upload video: ${uploadError.message}`);
          setUploading(false);
          return;
        }
      } else if (editMode && currentEditId) {
        // ✅ Keep old video URL if no new file selected during edit
        const old = videos.find(v => v.id === currentEditId);
        videoUrl = old?.videoUrl || '';
      }

      // 🔽 Upload thumbnail
      if (thumbnailFile) {
        try {
          const thumbnailFormData = new FormData();
          thumbnailFormData.append('file', thumbnailFile, thumbnailFile.name);
          thumbnailFormData.append('userId', currentUser.uid);
          thumbnailFormData.append('folder', 'thumbnails');

          const thumbnailResponse = await fetch('https://uploadimage-jzyeayelrq-uc.a.run.app', {
            method: 'POST',
            body: thumbnailFormData,
          });

          if (thumbnailResponse.ok) {
            const thumbnailData = await thumbnailResponse.json();
            thumbnailUrl = thumbnailData.url;
          }
        } catch (thumbnailError) {
          console.error('Thumbnail upload error:', thumbnailError);
        }
      } else if (editMode && currentEditId) {
        const old = videos.find(v => v.id === currentEditId);
        thumbnailUrl = old?.thumbnailUrl || '';
      }
    } else {
      // External video link
      videoUrl = videoLink;

      // Get YouTube thumbnail if it's a YouTube link
      if (videoLink.includes('youtube.com') || videoLink.includes('youtu.be')) {
        const videoId = extractYoutubeId(videoLink);
        if (videoId) {
          thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }
    }

    const videoData = {
      title: videoTitle,
      description: videoDescription,
      videoUrl,
      thumbnailUrl,
      category: videoCategory,
      partnerId: currentUser.uid,
      partnerName: currentUser.displayName || currentUser.email,
      createdAt: serverTimestamp(),
      type: uploadType === 'link' ? 'external' : 'uploaded',
      preference: parseInt(videoPreference)
    };

    if (editMode && currentEditId) {
      await updateDoc(doc(db, 'videos', currentEditId), videoData);
      setSuccess('Video updated successfully');
    } else {
      await addDoc(collection(db, 'videos'), videoData);
      setSuccess('Video uploaded successfully');
    }

    // ✅ Reset state after success
    setVideoFile(null);
    setThumbnailFile(null);
    setVideoTitle('');
    setVideoDescription('');
    setVideoLink('');
    setVideoCategory('workout');
    setVideoPreference('1');
    setUploadProgress(0);
    setShowUploadModal(false);
    setEditMode(false);
    setCurrentEditId(null);
    setThumbnailUrl('');

    loadVideos();
    setTimeout(() => setSuccess(''), 3000);
  } catch (err) {
    console.error('Error uploading video:', err);
    setError('Failed to upload video. Please try again.');
  } finally {
    setUploading(false);
  }
};

  
  const handleDeleteVideo = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'videos', videoId));
        
        setSuccess('Video deleted successfully');
        
        // Reload videos
        loadVideos();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
        
      } catch (err) {
        console.error('Error deleting video:', err);
        setError('Failed to delete video. Please try again.');
        setLoading(false);
      }
    }
  };
  
 const handleEditVideo = (video) => {
  setEditMode(true);
  setCurrentEditId(video.id);
  setVideoTitle(video.title || '');
  setVideoDescription(video.description || '');
  setVideoCategory(video.category || 'workout');
  setVideoPreference(video.preference?.toString() || '1');
  setThumbnailFile(null);
  setThumbnailUrl(video.thumbnailUrl || '');

  if (video.type === 'external') {
    setUploadType('link');
    setVideoLink(video.videoUrl || '');
  } else {
    setUploadType('file');
  }

  setShowUploadModal(true);
};

  
  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === 'video') {
      setIsDraggingVideo(true);
    } else {
      setIsDraggingThumbnail(true);
    }
  };
  
  const handleDragLeave = (e, type) => {
    e.preventDefault();
    if (type === 'video') {
      setIsDraggingVideo(false);
    } else {
      setIsDraggingThumbnail(false);
    }
  };
  
  const handleDrop = (e, type) => {
    e.preventDefault();
    
    if (type === 'video') {
      setIsDraggingVideo(false);
      const file = e.dataTransfer.files[0];
      if (file && file.type.includes('video/')) {
        setVideoFile(file);
      } else {
        setError('Please select a valid video file');
      }
    } else {
      setIsDraggingThumbnail(false);
      const file = e.dataTransfer.files[0];
      if (file && file.type.includes('image/')) {
        setThumbnailFile(file);
      } else {
        setError('Please select a valid image file for the thumbnail');
      }
    }
  };
  
  const extractYoutubeId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };
  
  const openVideo = (videoUrl) => {
    window.open(videoUrl, '_blank');
  };
  
  return (
    <Container>
      <Header>
        <Title>
          <FaVideo /> Video Management
        </Title>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {notifications > 0 && (
            <div 
              style={{ 
                position: 'relative',
                cursor: 'pointer',
                padding: '8px'
              }}
              onClick={() => navigate('/partner/chats')}
              title="You have unread messages"
            >
              <FaBell size={24} color="#6c63ff" />
              <span style={{
                position: 'absolute',
                top: '0',
                right: '0',
                background: '#dc3545',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {notifications}
              </span>
            </div>
          )}
          <AddButton onClick={() => {
            setEditMode(false);
            setCurrentEditId(null);
            setVideoFile(null);
            setThumbnailFile(null);
            setVideoTitle('');
            setVideoDescription('');
            setVideoLink('');
            setVideoCategory('workout');
            setShowUploadModal(true);
          }}>
            <FaPlus /> Upload New Video
          </AddButton>
        </div>
      </Header>
      
      {success && <SuccessDiv>{success}</SuccessDiv>}
      {error && <ErrorDiv>{error}</ErrorDiv>}
      
      {loading ? (
        <LoadingDiv>
          <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '2rem', marginRight: '1rem' }} />
          Loading videos...
        </LoadingDiv>
      ) : videos.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '5rem', color: '#ddd', marginBottom: '1rem' }}>
            <FaVideo />
          </div>
          <h3>No Videos Yet</h3>
          <p>Upload your first video to share with your clients</p>
          <AddButton 
            style={{ margin: '1.5rem auto', display: 'inline-flex' }}
            onClick={() => setShowUploadModal(true)}
          >
            <FaPlus /> Upload Video
          </AddButton>
        </EmptyState>
      ) : (
        <>
          <Card>
            <h3>Your Videos ({videos.length})</h3>
            <p>Upload and manage videos to share with your clients</p>
            
            <VideoGrid>
              {videos.map(video => (
                <VideoCard key={video.id}>
                  <VideoThumbnail>
                    <img 
                      src={video.thumbnailUrl || 'https://placehold.co/300x180?text=Video+Thumbnail'} 
                      alt={video.title} 
                    />
                    <PlayButton onClick={() => openVideo(video.videoUrl)}>
                      <FaYoutube />
                    </PlayButton>
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: video.type === 'external' ? 'rgba(0, 123, 255, 0.9)' : 'rgba(108, 99, 255, 0.9)',
                      color: 'white',
                      borderRadius: '4px',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      textTransform: 'uppercase'
                    }}>
                      {video.type === 'external' ? 'External' : 'Uploaded'}
                    </div>
                  </VideoThumbnail>
                  <VideoInfo>
                    <VideoTitle>{video.title}</VideoTitle>
                    <VideoDescription>
                      {video.description && video.description.length > 100
                        ? `${video.description.substring(0, 100)}...`
                        : video.description || 'No description available.'}
                    </VideoDescription>
                  </VideoInfo>
                  <VideoActions>
                    <span style={{ color: '#666', fontSize: '0.9rem' }}>
                      {video.createdAt?.toDate ? 
                        new Date(video.createdAt.toDate()).toLocaleDateString() : 
                        new Date().toLocaleDateString()}
                    </span>
                    <ActionButtons>
                      <ActionButton 
                        color="#007bff"
                        onClick={() => openVideo(video.videoUrl)}
                      >
                        <FaEye /> View
                      </ActionButton>
                      <ActionButton 
                        color="#6c63ff"
                        onClick={() => handleEditVideo(video)}
                      >
                        <FaEdit /> Edit
                      </ActionButton>
                      <ActionButton 
                        color="#dc3545"
                        onClick={() => handleDeleteVideo(video.id)}
                      >
                        <FaTrash /> Delete
                      </ActionButton>
                    </ActionButtons>
                  </VideoActions>
                </VideoCard>
              ))}
            </VideoGrid>
          </Card>
        </>
      )}
      
      {/* Upload Video Modal */}
      {showUploadModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <FaVideo />
                {editMode ? 'Edit Video' : 'Upload New Video'}
              </ModalTitle>
              <CloseButton onClick={() => setShowUploadModal(false)}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <VideoTypeTab>
              <TypeTabButton 
                active={uploadType === 'file'}
                onClick={() => setUploadType('file')}
              >
                Upload File
              </TypeTabButton>
              <TypeTabButton 
                active={uploadType === 'link'}
                onClick={() => setUploadType('link')}
              >
                Video URL
              </TypeTabButton>
            </VideoTypeTab>
            
            <form onSubmit={handleFileUpload}>
              {uploadType === 'file' && (
                <>
                  <FormGroup>
                    <Label>Video File {editMode && '(leave empty to keep current)'}</Label>
                    <FileDrop
                      isDragging={isDraggingVideo}
                      onDragOver={(e) => handleDragOver(e, 'video')}
                      onDragLeave={(e) => handleDragLeave(e, 'video')}
                      onDrop={(e) => handleDrop(e, 'video')}
                      onClick={() => document.getElementById('videoFileInput').click()}
                    >
                      {videoFile ? (
                        <div>
                          <FaCheck style={{ color: '#28a745', fontSize: '2rem', marginBottom: '1rem' }} />
                          <div>{videoFile.name}</div>
                          <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                            {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                          </div>
                        </div>
                      ) : (
                        <div>
                          <FaUpload style={{ fontSize: '2rem', marginBottom: '1rem', color: '#6c63ff' }} />
                          <div>Drag and drop your video file here or click to browse</div>
                          <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                            Supported formats: MP4, MOV, WebM
                          </div>
                        </div>
                      )}
                    </FileDrop>
                    <input
                      id="videoFileInput"
                      type="file"
                      accept="video/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          setVideoFile(e.target.files[0]);
                        }
                      }}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Thumbnail Image (optional)</Label>
                    <FileDrop
                      isDragging={isDraggingThumbnail}
                      onDragOver={(e) => handleDragOver(e, 'thumbnail')}
                      onDragLeave={(e) => handleDragLeave(e, 'thumbnail')}
                      onDrop={(e) => handleDrop(e, 'thumbnail')}
                      onClick={() => document.getElementById('thumbnailFileInput').click()}
                    >
                      {thumbnailFile ? (
  <div>
    <FaCheck style={{ color: '#28a745', fontSize: '2rem', marginBottom: '1rem' }} />
    <div>{thumbnailFile.name}</div>
    <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
      {(thumbnailFile.size / (1024 * 1024)).toFixed(2)} MB
    </div>
  </div>
) : thumbnailUrl ? (
  <div>
    <img 
      src={thumbnailUrl} 
      alt="Current Thumbnail" 
      style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '0.5rem' }} 
    />
    <div style={{ color: '#666', fontSize: '0.9rem' }}>
      Current thumbnail (upload to replace)
    </div>
  </div>
) : (
  <div>
    <FaUpload style={{ fontSize: '2rem', marginBottom: '1rem', color: '#6c63ff' }} />
    <div>Drag and drop your thumbnail image here or click to browse</div>
    <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
      Supported formats: JPG, PNG, WebP (16:9 recommended)
    </div>
  </div>
)}

                    </FileDrop>
                    <input
                      id="thumbnailFileInput"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          setThumbnailFile(e.target.files[0]);
                        }
                      }}
                    />
                  </FormGroup>
                </>
              )}
              
              {uploadType === 'link' && (
                <FormGroup>
                  <Label>Video URL</Label>
                  <Input
                    type="url"
                    placeholder="Enter YouTube, Vimeo, or other video URL"
                    value={videoLink}
                    onChange={(e) => setVideoLink(e.target.value)}
                  />
                  <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Example: https://www.youtube.com/watch?v=abcdefghijk
                  </div>
                </FormGroup>
              )}
              
              <FormGroup>
                <Label>Video Title</Label>
                <Input
                  type="text"
                  placeholder="Enter a descriptive title"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Description (optional)</Label>
                <TextArea
                  placeholder="Describe what this video contains"
                  value={videoDescription}
                  onChange={(e) => setVideoDescription(e.target.value)}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Display Order</Label>
                <Select
                  value={videoPreference}
                  onChange={(e) => setVideoPreference(e.target.value)}
                >
                  <option value="1">First</option>
                  <option value="2">Second</option>
                  <option value="3">Third</option>
                  <option value="4">Fourth</option>
                  <option value="5">Fifth</option>
                  <option value="6">Sixth</option>
                  <option value="7">Seventh</option>
                  <option value="8">Eighth</option>
                  <option value="9">Ninth</option>
                  <option value="10">Tenth</option>
                </Select>
                <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                  Select the order in which this video should appear
                </small>
              </FormGroup>
              
              <FormGroup>
                <Label>Category</Label>
                <Select
                  value={videoCategory}
                  onChange={(e) => setVideoCategory(e.target.value)}
                >
                  <option value="workout">Workout</option>
                  <option value="nutrition">Nutrition</option>
                  <option value="education">Educational</option>
                  <option value="motivation">Motivational</option>
                  <option value="technique">Technique</option>
                  <option value="other">Other</option>
                </Select>
              </FormGroup>
              
              {uploading && (
                <FormGroup>
                  <Label>Upload Progress</Label>
                  <ProgressBar>
                    <ProgressFill progress={uploadProgress} />
                  </ProgressBar>
                  <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                    {Math.round(uploadProgress)}%
                  </div>
                </FormGroup>
              )}
              
              <ButtonGroup>
                <CancelButton 
                  type="button" 
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  <FaTimes /> Cancel
                </CancelButton>
                <SaveButton 
                  type="submit"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Uploading...
                    </>
                  ) : (
                    <>
                      <FaSave /> {editMode ? 'Update Video' : 'Upload Video'}
                    </>
                  )}
                </SaveButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default VideosManagement;